'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useCallback, useEffect, useState } from 'react'
import { accountUrl } from '@/lib/config'
import { TrackedCTA } from '@/components/TrackedCTA'
import { useCtaTracking } from '@/hooks/useCtaTracking'
import type { PageContext } from '@/lib/cta-tracking'

// ========== TYPES ==========

export interface Benefit {
  icon: React.ReactNode
  title: string
  description: React.ReactNode
}

export interface Step {
  number: number
  title: string
  description: React.ReactNode
}

export interface UseCase {
  title: string
  icon: React.ReactNode
}

export interface FAQItem {
  question: string
  answer: string
}

export interface RelatedFeature {
  title: string
  description: string
  href: string
  icon: React.ReactNode
}

// ========== FEATURE HERO ==========

interface FeatureHeroProps {
  badge: string
  title: string
  heroText: string
  primaryCtaLabel: string
  primaryCtaHref?: string
  secondaryCtaLabel?: string
  secondaryCtaHref?: string
  bgImage?: string
  pageContext?: PageContext
}

export function FeatureHero({
  badge,
  title,
  heroText,
  primaryCtaLabel,
  primaryCtaHref,
  secondaryCtaLabel,
  secondaryCtaHref,
  bgImage,
  pageContext,
}: FeatureHeroProps) {
  const ctaHref = primaryCtaHref || `${accountUrl}/app/auth/register`
  const context: PageContext = pageContext || {
    page_type: badge?.toLowerCase().includes('business') ? 'business_feature' : 'feature',
    page_name: title,
    page_slug: title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
    page_category: badge?.toLowerCase().includes('business') ? 'business_feature' : 'feature',
  }

  useCtaTracking(context)

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#FFF8EC] via-white to-white pt-28 pb-20 md:pt-32 md:pb-24">
      {bgImage && (
        <div
          className="pointer-events-none absolute inset-0 bg-center bg-cover bg-no-repeat"
          style={{ backgroundImage: `url('${bgImage}')` }}
        />
      )}
      <div className="relative mx-auto max-w-4xl px-6 text-center">
        <span className="inline-block rounded-full bg-[#FFC933]/25 px-4 py-1.5 text-sm font-semibold text-[#2B0A4A] mb-5">
          {badge}
        </span>
        <h1 className="text-4xl font-bold leading-tight text-[#1B0044] md:text-5xl lg:text-[3.25rem]">
          {title}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-[#4F415A]">
          {heroText}
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <TrackedCTA
            href={ctaHref}
            pageContext={context}
            ctaContext={{
              cta_name: 'get_started',
              cta_label: primaryCtaLabel,
              cta_position: 'hero',
              cta_variant: 'primary',
            }}
            className="rounded-xl bg-[#1B0044] px-8 py-4 font-semibold text-white transition-colors hover:bg-[#2B0A4A]"
          >
            {primaryCtaLabel}
          </TrackedCTA>
          {secondaryCtaLabel && secondaryCtaHref && (
            <TrackedCTA
              href={secondaryCtaHref}
              pageContext={context}
              ctaContext={{
                cta_name: 'learn_more',
                cta_label: secondaryCtaLabel,
                cta_position: 'hero',
                cta_variant: 'secondary',
              }}
              className="rounded-xl border-2 border-[#1B0044] px-8 py-4 font-semibold text-[#1B0044] transition-colors hover:bg-[#FFF8EC]"
            >
              {secondaryCtaLabel}
            </TrackedCTA>
          )}
        </div>
      </div>
    </section>
  )
}

// ========== FEATURE BENEFITS ==========

interface FeatureBenefitsProps {
  heading?: string
  benefits: Benefit[]
}

export function FeatureBenefits({
  heading = 'Why Hosts Love This Feature',
  benefits,
}: FeatureBenefitsProps) {
  return (
    <section className="py-20 bg-white">
      <div className="mx-auto max-w-7xl px-6">
        <h2 className="text-3xl font-bold text-center text-[#1B0044] mb-12">{heading}</h2>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {benefits.map((benefit, i) => (
            <div
              key={i}
              className="rounded-2xl border border-[#E8DBD0] bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-[#FFC933] hover:shadow-md"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FFF8EC] text-[#2B0A4A] mb-4">
                {benefit.icon}
              </div>
              <h3 className="text-lg font-semibold text-[#1B0044] mb-2">{benefit.title}</h3>
              <p className="text-sm leading-relaxed text-[#4F415A]">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ========== HOW IT WORKS ==========

interface FeatureHowItWorksProps {
  id?: string
  subtext?: string
  steps: Step[]
  /** Optional demo video shown alongside the steps. */
  demo?: FeatureDemo
}

export function FeatureHowItWorks({
  id = 'how-it-works',
  subtext = 'Get started in minutes and create an unforgettable celebration experience.',
  steps,
  demo,
}: FeatureHowItWorksProps) {
  const [isDemoOpen, setIsDemoOpen] = useState(false)

  const stepsGrid = (
    <div
      className={`grid grid-cols-1 gap-8 sm:grid-cols-2 ${
        demo ? '' : `mx-auto ${steps.length === 3 ? 'lg:grid-cols-3 lg:max-w-4xl' : 'lg:grid-cols-4 lg:max-w-6xl'}`
      }`}
    >
      {steps.map((step, i) => (
        <div key={i} className={demo ? 'flex flex-col items-start text-left' : 'flex flex-col items-center text-center'}>
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#1B0044] text-white text-2xl font-bold mb-4 shadow-lg">
            {step.number}
          </div>
          <h3 className="text-lg font-semibold text-[#1B0044] mb-2">{step.title}</h3>
          <p className="text-sm leading-relaxed text-[#4F415A]">{step.description}</p>
        </div>
      ))}
    </div>
  )

  return (
    <section id={id} className="scroll-mt-24 py-20 bg-[#FFF8EC]">
      <div className="mx-auto max-w-7xl px-6">
        <h2 className="text-3xl font-bold text-center text-[#1B0044] mb-4">How It Works</h2>
        <p className="mx-auto mb-12 max-w-xl text-center text-[#4F415A]">{subtext}</p>
        {demo ? (
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            {stepsGrid}
            <DemoCard demo={demo} onOpen={() => setIsDemoOpen(true)} />
          </div>
        ) : (
          stepsGrid
        )}
      </div>

      {demo && isDemoOpen && <DemoModal demo={demo} onClose={() => setIsDemoOpen(false)} />}
    </section>
  )
}

// ========== OVERVIEW ==========

interface FeatureOverviewProps {
  heading?: string
  text: React.ReactNode
}

export function FeatureOverview({ heading = 'Overview', text }: FeatureOverviewProps) {
  return (
    <section className="bg-[#FFF8EC] py-16">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">{heading}</p>
        <p className="mt-4 text-base leading-relaxed text-[#1B0044]/80 md:text-lg">{text}</p>
      </div>
    </section>
  )
}

// ========== CAPABILITIES ==========

interface FeatureCapabilitiesProps {
  heading?: string
  items: string[]
}

export function FeatureCapabilities({ heading = 'Key Capabilities', items }: FeatureCapabilitiesProps) {
  return (
    <section id="capabilities" className="scroll-mt-24 bg-white py-16">
      <div className="mx-auto max-w-3xl px-6">
        <h2 className="mb-8 text-center text-3xl font-bold text-[#1B0044]">{heading}</h2>
        <div className="rounded-2xl border border-[#E8DBD0] bg-[#FFF8EC] p-7">
          <ul className="space-y-3">
            {items.map((item) => (
              <li key={item} className="flex items-start gap-3 text-[#1B0044]/85">
                <span className="mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-[#FFC933]" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

// ========== DEMO GALLERY ==========

export interface FeatureDemo {
  title: string
  /** Path to an mp4 under /public */
  src: string
  /** Path to a poster/thumbnail image under /public */
  poster: string
  description?: string
}

interface FeatureDemoGalleryProps {
  heading?: string
  subtext?: string
  demos: FeatureDemo[]
}

function PlayIcon({ className = 'h-6 w-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  )
}

function DemoCard({ demo, onOpen }: { demo: FeatureDemo; onOpen: () => void }) {
  return (
    <figure className="overflow-hidden rounded-2xl border border-[#E8DBD0] bg-white shadow-sm transition-all hover:-translate-y-1 hover:border-[#FFC933] hover:shadow-md">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Play ${demo.title} demo`}
        className="group relative block aspect-[128/60] w-full overflow-hidden bg-[#1B0044]"
      >
        <Image
          src={demo.poster}
          alt={`${demo.title} demo preview`}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          className="object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <span className="absolute inset-0 bg-[#1B0044]/20 transition-colors group-hover:bg-[#1B0044]/35" />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex items-center gap-2 rounded-full bg-white/95 px-5 py-2.5 font-semibold text-[#1B0044] shadow-lg transition-transform group-hover:scale-105">
            <PlayIcon className="h-5 w-5 text-[#FF8A3D]" />
            View
          </span>
        </span>
      </button>
      <figcaption className="p-5">
        <h3 className="text-base font-semibold text-[#1B0044]">{demo.title}</h3>
        {demo.description && (
          <p className="mt-1 text-sm leading-relaxed text-[#4F415A]">{demo.description}</p>
        )}
      </figcaption>
    </figure>
  )
}

function DemoModal({ demo, onClose }: { demo: FeatureDemo; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${demo.title} demo`}
      onClick={onClose}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
    >
      <div className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute -top-3 -right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#1B0044] shadow-lg transition-colors hover:bg-[#FFF8EC]"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <video
          key={demo.src}
          src={demo.src}
          className="w-full rounded-2xl bg-black shadow-2xl"
          controls
          autoPlay
          playsInline
          aria-label={`${demo.title} demo`}
        />
        <p className="mt-3 text-center text-sm font-medium text-white/90">{demo.title}</p>
      </div>
    </div>
  )
}

export function FeatureDemoGallery({
  heading = 'See It in Action',
  subtext,
  demos,
}: FeatureDemoGalleryProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const close = useCallback(() => setActiveIndex(null), [])
  const activeDemo = activeIndex === null ? null : demos[activeIndex]

  return (
    <section id="see-it-in-action" className="scroll-mt-24 bg-[#FFF8EC] py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-6">
        <h2 className="text-center text-3xl font-bold text-[#1B0044]">{heading}</h2>
        {subtext && (
          <p className="mx-auto mt-4 max-w-2xl text-center text-[#4F415A]">{subtext}</p>
        )}
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {demos.map((demo, i) => (
            <DemoCard key={demo.src} demo={demo} onOpen={() => setActiveIndex(i)} />
          ))}
        </div>
      </div>

      {activeDemo && <DemoModal demo={activeDemo} onClose={close} />}
    </section>
  )
}

// ========== USE CASES ==========

interface FeatureUseCasesProps {
  useCases: UseCase[]
}

export function FeatureUseCases({ useCases }: FeatureUseCasesProps) {
  return (
    <section className="py-20 bg-white">
      <div className="mx-auto max-w-7xl px-6">
        <h2 className="text-3xl font-bold text-center text-[#1B0044] mb-4">Perfect For</h2>
        <p className="mx-auto mb-12 max-w-2xl text-center text-[#4F415A]">
          This feature works beautifully across all kinds of celebrations and events.
        </p>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {useCases.map((useCase, i) => (
            <div
              key={i}
              className="flex flex-col items-center gap-3 rounded-2xl border border-[#E8DBD0] p-4 text-center transition-all hover:-translate-y-1 hover:border-[#FFC933] hover:bg-[#FFF8EC]"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FFF8EC] text-[#2B0A4A]">
                {useCase.icon}
              </div>
              <span className="text-sm font-medium text-[#1B0044]">{useCase.title}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ========== TRUST SECTION ==========

export function FeatureTrust() {
  return (
    <section className="border-y border-[#E8DBD0] bg-gradient-to-r from-[#FFF8EC] to-white py-12">
      <div className="mx-auto max-w-5xl px-6">
        <div className="flex flex-wrap justify-center items-center gap-8">
          <div className="flex items-center gap-3">
            <svg className="h-8 w-8 text-[#2B0A4A]" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-semibold text-[#2B0A4A]">Private & Secure</span>
          </div>
          <div className="flex items-center gap-3">
            <svg className="h-8 w-8 text-[#2B0A4A]" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />
            </svg>
            <span className="font-semibold text-[#2B0A4A]">No App Required</span>
          </div>
          <div className="flex items-center gap-3">
            <svg className="h-8 w-8 text-[#2B0A4A]" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
            </svg>
            <span className="font-semibold text-[#2B0A4A]">Works for Any Event Size</span>
          </div>
          <div className="flex items-center gap-3">
            <svg className="h-8 w-8 text-[#2B0A4A]" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-semibold text-[#2B0A4A]">Ready in Minutes</span>
          </div>
        </div>
      </div>
    </section>
  )
}

// ========== FAQ ==========

interface FeatureFAQProps {
  faqs: FAQItem[]
}

export function FeatureFAQ({ faqs }: FeatureFAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section className="py-20 bg-[#FFF8EC]">
      <div className="mx-auto max-w-3xl px-6">
        <h2 className="text-3xl font-bold text-center text-[#1B0044] mb-12">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="overflow-hidden rounded-2xl border border-[#E8DBD0] bg-white shadow-sm"
            >
              <button
                className="flex w-full items-center justify-between px-6 py-5 text-left"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                aria-expanded={openIndex === i}
              >
                <span className="font-semibold text-[#1B0044] pr-4">{faq.question}</span>
                <svg
                  className={`h-5 w-5 flex-shrink-0 text-[#FF8A3D] transition-transform ${
                    openIndex === i ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openIndex === i && (
                <div className="border-t border-[#E8DBD0] px-6 pb-5 pt-4 leading-relaxed text-[#4F415A]">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ========== FEATURE CTA ==========

interface FeatureCTAProps {
  heading: string
  subtext: string
  primaryCtaLabel: string
  primaryCtaHref?: string
  secondaryCtaLabel?: string
  secondaryCtaHref?: string
  pageContext?: PageContext
}

export function FeatureCTA({
  heading,
  subtext,
  primaryCtaLabel,
  primaryCtaHref,
  secondaryCtaLabel,
  secondaryCtaHref,
  pageContext,
}: FeatureCTAProps) {
  const ctaHref = primaryCtaHref || `${accountUrl}/app/auth/register`
  const context: PageContext = pageContext || {
    page_type: 'feature',
    page_name: heading,
    page_slug: heading.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
    page_category: 'feature',
  }

  return (
    <section className="py-20 bg-gradient-to-r from-[#1B0044] to-[#2B0A4A]">
      <div className="mx-auto max-w-4xl px-6 text-center">
        <h2 className="text-3xl font-bold text-white md:text-4xl">{heading}</h2>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-[#FFF8EC]/80">{subtext}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <TrackedCTA
            href={ctaHref}
            pageContext={context}
            ctaContext={{
              cta_name: 'get_started',
              cta_label: primaryCtaLabel,
              cta_position: 'bottom',
              cta_variant: 'primary',
            }}
            className="rounded-xl bg-[#FFC933] px-8 py-4 font-semibold text-[#1B0044] transition-colors hover:bg-[#FF8A3D] hover:text-white"
          >
            {primaryCtaLabel}
          </TrackedCTA>
          {secondaryCtaLabel && secondaryCtaHref && (
            <TrackedCTA
              href={secondaryCtaHref}
              pageContext={context}
              ctaContext={{
                cta_name: 'learn_more',
                cta_label: secondaryCtaLabel,
                cta_position: 'bottom',
                cta_variant: 'secondary',
              }}
              className="rounded-xl border-2 border-white px-8 py-4 font-semibold text-white transition-colors hover:bg-white/10"
            >
              {secondaryCtaLabel}
            </TrackedCTA>
          )}
        </div>
      </div>
    </section>
  )
}

// ========== RELATED FEATURES ==========

export const ALL_FEATURES: RelatedFeature[] = [
  {
    title: 'Video Wishes',
    description: 'Collect heartfelt video messages from friends, family, and guests anywhere in the world.',
    href: '/personal/features/video-wishes',
    icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.069A1 1 0 0121 8.845v6.31a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>,
  },
  {
    title: 'Digital Invitations',
    description: 'Create beautiful personalized event invitations with real-time RSVP tracking.',
    href: '/personal/features/digital-invitations',
    icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
  },
  {
    title: 'RSVP Management',
    description: 'Track guest responses, send reminders, and plan your event with real-time RSVP data.',
    href: '/personal/features/rsvp-management',
    icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>,
  },
  {
    title: 'Event Photo Sharing',
    description: 'Let guests upload photos and videos to your event gallery via QR code.',
    href: '/personal/features/event-photo-sharing',
    icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /></svg>,
  },
  {
    title: 'Live Event Wall',
    description: 'Display video wishes and photos on a live screen during your celebration.',
    href: '/personal/features/live-event-wall',
    icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
  },
  {
    title: 'QR Code Event Access',
    description: 'Generate event QR codes guests can scan to access your event page instantly.',
    href: '/personal/features/event-qr-codes',
    icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>,
  },
  // HIDDEN: Gift Cards (not launching yet)
  // {
  //   title: 'Gift Cards',
  //   description: 'Let guests send digital gift cards directly to the event host as part of the celebration.',
  //   href: '/features/gift-cards',
  //   icon: <svg ...>,
  // },
  {
    title: 'Gifting',
    description: 'Give the gift of a celebration — send a VibeVite plan to someone special.',
    href: '/personal/features/gift-vibevite',
    icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>,
  },
]

interface RelatedFeaturesProps {
  currentHref: string
}

export function RelatedFeatures({ currentHref }: RelatedFeaturesProps) {
  const features = ALL_FEATURES.filter((f) => f.href !== currentHref)
  return (
    <section className="py-20 bg-white">
      <div className="mx-auto max-w-7xl px-6">
        <h2 className="text-3xl font-bold text-center text-[#1B0044] mb-4">Explore More Features</h2>
        <p className="text-center text-gray-600 mb-12 max-w-xl mx-auto">
          VibeVite has everything you need to create an unforgettable celebration.
        </p>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, i) => (
            <Link
              key={i}
              href={feature.href}
              className="group rounded-2xl border border-gray-100 p-6 bg-white shadow-sm hover:shadow-md hover:border-[#FFC933] transition-all"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FFF8EC] text-[#2B0A4A] mb-4 group-hover:bg-[#FFC933] group-hover:text-white transition-colors">
                {feature.icon}
              </div>
              <h3 className="text-lg font-semibold text-[#1B0044] mb-2">{feature.title}</h3>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">{feature.description}</p>
              <span className="text-sm font-semibold text-[#FF8A3D] group-hover:underline">
                Learn More →
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

// ========== RELATED BUSINESS FEATURES ==========

export const ALL_BUSINESS_FEATURES: RelatedFeature[] = [
  {
    title: 'Employee Recognition Walls',
    description: 'Give appreciation a shared, collaborative place the whole team can contribute to.',
    href: '/business/features/employee-recognition-walls',
    icon: <span className="text-xl">🏆</span>,
  },
  {
    title: 'Milestone Templates',
    description: 'Ready-made templates for birthdays, anniversaries, promotions, retirements, and farewells.',
    href: '/business/features/milestone-templates',
    icon: <span className="text-xl">🎉</span>,
  },
  {
    title: 'Multimedia Contributions',
    description: 'Collect messages, photos, video, audio, and GIFs in one collaborative wall.',
    href: '/business/features/multimedia-contributions',
    icon: <span className="text-xl">🎥</span>,
  },
  {
    title: 'Invitations & Reminders',
    description: 'Invite contributors with a secure link and follow up automatically.',
    href: '/business/features/invitations-reminders',
    icon: <span className="text-xl">✉️</span>,
  },
  {
    title: 'Moderation & Admin',
    description: 'Review contributions before they go live and manage roles across your organization.',
    href: '/business/features/moderation-admin',
    icon: <span className="text-xl">🛡️</span>,
  },
  {
    title: 'SSO & Access Control',
    description: 'Enterprise identity management and role-based access across your organization.',
    href: '/business/features/sso-access-control',
    icon: <span className="text-xl">🔐</span>,
  },
]

interface RelatedBusinessFeaturesProps {
  currentHref: string
}

export function RelatedBusinessFeatures({ currentHref }: RelatedBusinessFeaturesProps) {
  const features = ALL_BUSINESS_FEATURES.filter((f) => f.href !== currentHref)
  return (
    <section className="py-20 bg-white">
      <div className="mx-auto max-w-7xl px-6">
        <h2 className="text-3xl font-bold text-center text-[#1B0044] mb-4">Explore More VibeWall Features</h2>
        <p className="text-center text-gray-600 mb-12 max-w-xl mx-auto">
          Everything HR teams and managers need to build a culture of recognition.
        </p>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <Link
              key={feature.href}
              href={feature.href}
              className="group rounded-2xl border border-gray-100 p-6 bg-white shadow-sm hover:shadow-md hover:border-[#FFC933] transition-all"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FFF8EC] text-[#2B0A4A] mb-4 group-hover:bg-[#FFC933] transition-colors">
                {feature.icon}
              </div>
              <h3 className="text-lg font-semibold text-[#1B0044] mb-2">{feature.title}</h3>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">{feature.description}</p>
              <span className="text-sm font-semibold text-[#FF8A3D] group-hover:underline">
                Learn More →
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-4 text-center">
          <Link href="/business" className="text-sm font-semibold text-[#2B0A4A] underline underline-offset-4 hover:text-[#FF8A3D]">
            ← Back to VibeWall for Business
          </Link>
        </div>
      </div>
    </section>
  )
}
